import type { TableAction, TableState } from "../tables/types";
import { makeRegionPreviewID } from "../regions/utils";
import {
  deleteSelectedCols,
  deleteSelectedRows,
  divideCol,
  divideRow,
  toggleCellSelection,
  mergeSelectedCells,
  moveX,
  moveY,
  resizeCol,
  resizeRow,
  selectCells,
  splitSelectedCells,
  updateGroupOfSelectedCells,
  updateHeadersOfSelectedCells,
  updateRoleOfSelectedCells,
  updateScopeOfSelectedCells,
} from "../tables/mutations";

export type CommonEditorTab =
  | "metadata"
  | "pages"
  | "regions"
  | "attributes"
  | "export";

export type RegionEditorTab =
  | "update"
  | "artifact"
  | "caption"
  | "figure"
  | "formula"
  | "heading"
  | "hybrid"
  | "image"
  | "list"
  | "listItem"
  | "sublist"
  | "table"
  | "text"
  | "toc";

export type EditorTab = CommonEditorTab | RegionEditorTab;

export type RegionMode = "rectangle" | "polygon";

export type SwitchRegionModeEditorAction = {
  type: "switchMode";
  mode: RegionMode;
};

export type SelectRegionEditorAction = {
  type: "selectRegion";
  regionId: string;
};

export type DeselectRegionEditorAction = {
  type: "deselectRegion";
};

export type SwitchToCommonTab = {
  type: "switchToCommonTab";
  tab: CommonEditorTab;
};

export type SwitchToRegionTab = {
  type: "switchToRegionTab";
  tab: RegionEditorTab;
  regionId: string;
  table?: TableState;
};

export type EditorAction =
  | SwitchRegionModeEditorAction
  | SelectRegionEditorAction
  | DeselectRegionEditorAction
  | SwitchToCommonTab
  | SwitchToRegionTab
  | TableAction;

type CommonTabEditorState = {
  tab: CommonEditorTab;
  mode: RegionMode;
  selectedRegionId?: string;
  table: undefined;
};

type RegionTabEditorState = {
  tab: RegionEditorTab;
  mode: RegionMode;
  selectedRegionId: string;
  table?: TableState;
};

export type EditorState = CommonTabEditorState | RegionTabEditorState;

export function editorReducer(
  state: EditorState,
  action: EditorAction,
): EditorState {
  if (action.type === "switchMode") {
    return {
      ...state,
      mode: action.mode,
    };
  }

  if (action.type === "selectRegion") {
    scrollToRegion(action.regionId);

    return {
      ...state,
      selectedRegionId: action.regionId,
    };
  }

  if (action.type === "deselectRegion") {
    if (
      state.tab === "metadata" ||
      state.tab === "pages" ||
      state.tab === "regions" ||
      state.tab === "attributes" ||
      state.tab === "export"
    ) {
      return {
        ...state,
        selectedRegionId: undefined,
      };
    }

    return {
      ...state,
      tab: "regions",
      selectedRegionId: undefined,
      table: undefined,
    };
  }

  if (action.type === "switchToCommonTab") {
    return {
      ...state,
      tab: action.tab,
      table: undefined,
    };
  }

  if (action.type === "switchToRegionTab") {
    scrollToRegion(action.regionId);

    return {
      ...state,
      tab: action.tab,
      selectedRegionId: action.regionId,
      table: action.table,
    };
  }

  if (state.table) {
    let tableState: TableState = JSON.parse(JSON.stringify(state.table));

    switch (action.type) {
      case "moveTableX":
        tableState = moveX(tableState, action.x, action.absolute);
        break;
      case "moveTableY":
        tableState = moveY(tableState, action.y, action.absolute);
        break;
      case "resizeTableRow":
        tableState = resizeRow(tableState, action.index, action.height);
        break;
      case "resizeTableCol":
        tableState = resizeCol(tableState, action.index, action.width);
        break;
      case "mergeTableCell":
        tableState = mergeSelectedCells(tableState);
        break;
      case "splitTableCell":
        tableState = splitSelectedCells(tableState);
        break;
      case "divideTableRow":
        tableState = divideRow(tableState, action.divisor);
        break;
      case "divideTableCol":
        tableState = divideCol(tableState, action.divisor);
        break;
      case "deleteTableRows":
        tableState = deleteSelectedRows(tableState);
        break;
      case "deleteTableCols":
        tableState = deleteSelectedCols(tableState);
        break;
      case "updateTableCellRoles":
        tableState = updateRoleOfSelectedCells(tableState, action.role);
        break;
      case "updateTableCellScopes":
        tableState = updateScopeOfSelectedCells(tableState, action.scope);
        break;
      case "updateTableRowGroups":
        tableState = updateGroupOfSelectedCells(tableState, action.group);
        break;
      case "updateTableCellHeaders":
        tableState = updateHeadersOfSelectedCells(
          tableState,
          action.addedEncodedHeaders,
          action.removedEncodedHeaders,
        );
        break;
      case "selectTableCells":
        tableState = selectCells(tableState, action.start, action.end);
        break;
      case "toggleTableCellSelection":
        tableState = toggleCellSelection(tableState, action.cellIdentifier);
        break;
      case "predictTable":
        tableState = {
          ...tableState,
          ...action.prediction,
          position: {
            x: Math.floor(action.prediction.position.x),
            y: Math.floor(action.prediction.position.y),
          },
        };
        break;
    }

    return {
      ...state,
      table: tableState,
    };
  }

  return state;
}

export function makeTabIdentifier(tab: EditorTab, nodeId: string): string {
  return `${tab}-${nodeId}`;
}

export function inferTabIdentifier(state: EditorState): string {
  if (
    state.tab === "update" ||
    state.tab === "artifact" ||
    state.tab === "caption" ||
    state.tab === "figure" ||
    state.tab === "formula" ||
    state.tab === "heading" ||
    state.tab === "hybrid" ||
    state.tab === "image" ||
    state.tab === "list" ||
    state.tab === "listItem" ||
    state.tab === "sublist" ||
    state.tab === "table" ||
    state.tab === "text" ||
    state.tab === "toc"
  ) {
    return makeTabIdentifier(state.tab, state.selectedRegionId);
  }

  return state.tab;
}

function scrollToRegion(regionId: string, instant = false) {
  setTimeout(() => {
    document.getElementById(makeRegionPreviewID(regionId))?.scrollIntoView({
      behavior: instant ? "instant" : "smooth",
      block: "center",
    });
  });
}
